import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronRight, Navigation, Star, Sparkles } from "lucide-react";
import api from "../../lib/api";
import CustomerTabBar from "../../components/CustomerTabBar";
import { useGeo, distanceKm, fmtDistance } from "../../lib/homeMode";

// Categorie essenziali (blueprint). "Attività vicine" NON è una categoria: è il filtro geografico.
const CATEGORIES = [
  { id: "mangiare", label: "Mangiare", emoji: "🍕", to: "/lista/food", match: ["ristor", "pizz", "food", "bar", "trattoria", "osteria", "sushi", "hambur", "kebab", "gelat", "pasticc", "caff", "pub", "enotec"] },
  { id: "negozi", label: "Negozi", emoji: "🛍️", to: "/lista/attivita", match: ["negoz", "botteg", "aliment", "abbigl", "fioraio", "libreria", "ferrament", "market"] },
  { id: "professionisti", label: "Professionisti", emoji: "🧑‍🔧", to: "/professionisti", match: ["idraul", "elettr", "tecnic", "artigian", "profession"] },
  { id: "servizi", label: "Servizi", emoji: "🛵", to: "/app/mercato", match: ["serviz", "consegn", "spesa", "commission", "lavand"] },
  { id: "esperienze", label: "Esperienze", emoji: "✨", to: "/esperienze", match: [] },
  { id: "eventi", label: "Eventi", emoji: "🎟️", to: "/lista/eventi", match: [] },
];

export default function EsploraHub() {
  const navigate = useNavigate();
  const geo = useGeo();
  const [pois, setPois] = useState([]);
  const [q, setQ] = useState("");
  const [nearOnly, setNearOnly] = useState(false);
  const [cat, setCat] = useState(null);

  useEffect(() => {
    api.get("/public/map").then(({ data }) => setPois((data.pois || []).filter((p) => p.lat != null))).catch(() => {});
  }, []);

  const results = useMemo(() => {
    let list = pois.map((p) => ({ ...p, _d: geo ? distanceKm(geo, { lat: p.lat, lng: p.lng }) : null }));
    if (cat) {
      const c = CATEGORIES.find((x) => x.id === cat);
      if (c && c.match.length) list = list.filter((p) => c.match.some((m) => (p.category || "").toLowerCase().includes(m)));
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s));
    }
    if (nearOnly && geo) list = list.filter((p) => p._d != null && p._d <= 2);
    if (geo) list.sort((a, b) => (a._d ?? 999) - (b._d ?? 999));
    return list.slice(0, 12);
  }, [pois, q, nearOnly, cat, geo]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-28" data-testid="esplora-page">
      <div className="max-w-md mx-auto px-5">
        <div className="pt-5">
          <h1 className="font-['Unbounded'] font-black text-2xl tracking-tight">Esplora il <span className="text-[#00E676]">quartiere</span></h1>
        </div>

        {/* Anteprima mappa compatta */}
        <button
          data-testid="esplora-map-preview"
          onClick={() => navigate("/esplora/mappa")}
          className="mt-4 w-full h-32 rounded-2xl overflow-hidden relative border border-white/10 active:scale-[0.99] transition-transform"
          style={{ background: "radial-gradient(120% 120% at 20% 10%, rgba(0,230,118,.18), transparent 55%), radial-gradient(100% 100% at 90% 90%, rgba(125,249,255,.12), transparent 50%), #101014" }}
        >
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
          <span className="absolute left-6 top-8 h-2.5 w-2.5 rounded-full bg-[#00E676] shadow-[0_0_10px_#00E676]" />
          <span className="absolute left-24 top-16 h-2 w-2 rounded-full bg-[#7DF9FF] shadow-[0_0_8px_#7DF9FF]" />
          <span className="absolute right-16 top-10 h-2 w-2 rounded-full bg-[#00E676] shadow-[0_0_8px_#00E676]" />
          <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent">
            <span className="text-sm font-semibold flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#00E676]" /> Mappa del quartiere</span>
            <span className="text-xs text-[#00E676] font-semibold flex items-center gap-0.5">Apri <ChevronRight className="h-3.5 w-3.5" /></span>
          </div>
        </button>

        {/* Barra ricerca */}
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-[#121215] border border-white/10 px-3.5 py-3 focus-within:border-[#00E676]/40 transition-colors">
          <Search className="h-4 w-4 text-white/40 shrink-0" />
          <input
            data-testid="esplora-search-bar"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca locali, prodotti o servizi in zona..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
          />
        </div>

        {/* Filtro geografico "Vicino a me" */}
        <button
          data-testid="filter-vicino-toggle"
          onClick={() => setNearOnly((v) => !v)}
          className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${nearOnly ? "bg-[#00E676] text-black border-[#00E676]" : "border-white/15 text-white/70"}`}
        >
          <Navigation className="h-3.5 w-3.5" /> Vicino a me · &lt; 2 km
        </button>

        {/* Categorie */}
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="esplora-categories">
          {CATEGORIES.map((c) => {
            const active = cat === c.id;
            return (
              <div key={c.id} className="flex flex-col">
                <button
                  data-testid={`cat-tile-${c.id}`}
                  onClick={() => setCat(active ? null : c.id)}
                  className={`aspect-square rounded-2xl bg-[#121215] border flex flex-col items-center justify-center gap-1.5 transition-colors active:scale-95 ${active ? "border-[#00E676]/60" : "border-white/8 hover:border-white/15"}`}
                >
                  <span className="text-2xl">{c.emoji}</span>
                  <span className={`text-[11px] font-semibold ${active ? "text-[#00E676]" : "text-white/70"}`}>{c.label}</span>
                </button>
              </div>
            );
          })}
        </div>
        {cat && (
          <button data-testid="esplora-open-category" onClick={() => navigate(CATEGORIES.find((x) => x.id === cat).to)} className="mt-3 w-full text-center text-sm text-[#00E676] font-medium flex items-center justify-center gap-1">
            Apri tutti · {CATEGORIES.find((x) => x.id === cat).label} <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {/* Risultati consigliati */}
        <div className="mt-7" data-testid="esplora-recommended-list">
          <h2 className="font-['Unbounded'] font-bold text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#00E676]" /> Consigliati</h2>
          <div className="mt-3 space-y-2">
            {results.map((p) => (
              <button key={p.id} data-testid={`esplora-result-${p.id}`} onClick={() => navigate(`/attivita/${p.id}`)} className="w-full text-left flex items-center gap-3 rounded-2xl bg-[#121215] border border-white/5 hover:border-[#00E676]/30 p-3 transition-colors active:scale-[0.99]">
                <div className="h-12 w-12 rounded-xl bg-[#1A1A1E] overflow-hidden flex items-center justify-center shrink-0">
                  {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <MapPin className="h-5 w-5 text-white/25" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-white/40 truncate flex items-center gap-1.5">
                    <span className="truncate">{p.category}</span>
                    {p._d != null && <span>· {fmtDistance(p._d)}</span>}
                    {p.rating > 0 && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-[#FFD700]" /> {p.rating}</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-white/20 shrink-0" />
              </button>
            ))}
            {results.length === 0 && <div className="text-white/30 text-sm py-8 text-center">Nessun risultato per questo filtro.</div>}
          </div>
        </div>
      </div>
      <CustomerTabBar active="esplora" />
    </div>
  );
}
